import React, { useMemo, useState, useCallback } from 'react';
import { Box, Pagination, TextField, Typography } from '@mui/material';
import {
  useMetadata,
  FieldMetadata,
} from '../../../../contexts/MetadataContext';
import { useDebounce } from '../../../../hooks/useDebounce';
import HelpTextFieldItem from './HelpTextFieldItem';
import HelpTextPreviewDrawer from './HelpTextPreviewDrawer';

const ITEMS_PER_PAGE = 10;

interface HelpTextEditorProps {
  resourceType: string;
  helpText: Record<string, string>;
  onHelpTextChange: (helpText: Record<string, string>) => void;
}

const HelpTextEditor: React.FC<HelpTextEditorProps> = ({
  resourceType,
  helpText,
  onHelpTextChange,
}) => {
  const { fields } = useMetadata();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewField, setPreviewField] = useState<FieldMetadata | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const allFields = fields();

  // Filter fields by resource type and search query
  const filteredFields = useMemo(() => {
    if (!allFields) return [];

    return allFields.filter((field) => {
      // Filter by resource type
      if (field.ResourceType !== resourceType) return false;

      // Filter by search query
      if (!debouncedSearch) return true;
      const query = debouncedSearch.toLowerCase();
      return (
        field.Key.toLowerCase().includes(query) ||
        field.LocaleKey.toLowerCase().includes(query) ||
        field.Name.toLowerCase().includes(query)
      );
    });
  }, [allFields, resourceType, debouncedSearch]);

  // Reset page when search changes
  useMemo(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Paginate
  const totalPages = Math.ceil(filteredFields.length / ITEMS_PER_PAGE);
  const paginatedFields = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredFields.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredFields, page]);

  const getHelpTextKey = (field: FieldMetadata) =>
    `${field.ResourceType}.${field.Key}`;

  const handleHelpTextChange = useCallback(
    (field: FieldMetadata, value: string) => {
      const key = getHelpTextKey(field);
      const updated = { ...helpText };

      if (value.trim()) {
        updated[key] = value;
      } else {
        delete updated[key];
      }

      onHelpTextChange(updated);
    },
    [helpText, onHelpTextChange],
  );

  const handlePreview = useCallback((field: FieldMetadata) => {
    setPreviewField(field);
    setPreviewOpen(true);
  }, []);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {resourceType} Help Text
        </Typography>
        <TextField
          fullWidth
          placeholder="Search fields by Key, LocaleKey, or Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
        />
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {filteredFields.length} fields
          {debouncedSearch && ` matching "${debouncedSearch}"`}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {paginatedFields.map((field) => {
          const key = getHelpTextKey(field);
          return (
            <HelpTextFieldItem
              key={field.Key}
              field={field}
              value={helpText[key] || ''}
              onChange={(value) => handleHelpTextChange(field, value)}
              onPreview={() => handlePreview(field)}
            />
          );
        })}

        {paginatedFields.length === 0 && (
          <Typography
            sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
          >
            No fields found{debouncedSearch && ` matching "${debouncedSearch}"`}
            .
          </Typography>
        )}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <HelpTextPreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fieldLabel={previewField?.Name || ''}
        helpText={
          previewField ? helpText[getHelpTextKey(previewField)] || '' : ''
        }
      />
    </Box>
  );
};

export default HelpTextEditor;
